/**
 * Every contact address the product points customers at, in one place.
 *
 * Isomorphic and secret-free, so client components, server error strings, and
 * MDX content can all use the same values. Before this existed, `hello@` was
 * hardcoded in the footer and three separate places told users to "contact
 * support" without ever giving them a channel.
 *
 * IMPORTANT: every address here must be a real, monitored mailbox. Shipping copy
 * that points at an address nobody reads is worse than shipping no copy.
 */
export const CONTACT = {
  /** General/marketing inbound. */
  general: 'hello@masklabs.io',
  /** Existing customers: billing questions, invoiced terms, credit, deletion. */
  support: 'support@masklabs.io',
  /** Pre-sale: custom pricing, enterprise terms, paying by invoice. */
  sales: 'sales@masklabs.io',
  legal: 'legal@masklabs.io',
  abuse: 'abuse@masklabs.io',
} as const

/**
 * Business phone, shown alongside the registered address in the footer. Two
 * forms because they differ: `display` is what a human reads, `tel` is the
 * E.164 digits a dialler needs — deriving one from the other would mean
 * stripping punctuation at every call site.
 */
export const PHONE = {
  display: '+1 (307) 423-9855',
  tel: '+13074239855',
} as const

/**
 * The support page, optionally with a topic preselected (see
 * lib/support/support.ts for the topic ids). This is the channel the product
 * points people at: it captures the account context from the session, lands in
 * the DB where support can see it, and works for people on webmail whose
 * browser does nothing with a `mailto:`.
 *
 * The addresses above are still real and still monitored — the form shows
 * `support@` as a fallback, and the legal/abuse addresses stay the contractual
 * notice channels — but a "contact us" affordance in the product should send
 * people here.
 */
export const SUPPORT_PATH = '/support'

export function supportPath(topic?: string): string {
  return topic ? `${SUPPORT_PATH}?topic=${topic}` : SUPPORT_PATH
}

/** Bare-domain form for prose (error messages, emails), where a full URL with
 *  scheme reads like spam and a relative path is meaningless. */
export const SUPPORT_PAGE_DISPLAY = 'masklabs.io/support'
